import { useSearchParams } from 'react-router-dom';
import { readPreparedExport, type PreparedCampaignExport } from '../../lib/campaign-export-filters';
import React, { useState, useEffect } from 'react';
import type { InterviewSession, InterviewData } from '../../types';
import { getCampaignDisplayStatus, getResponseDisplayStatus } from '../../lib/admin-utils';
import { StatusBadge, DropdownMenu, Skeleton } from '../ui';
import { DownloadIcon } from '../icons/DownloadIcon';
import { useOrg } from '../../lib/OrgProvider';
import {
  IconBan,
  IconCopy,
  IconPencil,
  IconTrash,
  IconDotsVertical,
  IconMessage,
  IconBolt,
  IconUserPlus,
} from '@tabler/icons-react';
import { PlayIcon } from '@/components/icons';
import { InviteModal } from './InviteModal';
import type { AddParticipantsHandler } from '../../services/automationService';

const CampaignExportModal = React.lazy(async () => {
  const module = await import('./CampaignExportModal');
  return { default: module.CampaignExportModal };
});

/** Props for {@link CampaignHeader}. */
export interface CampaignHeaderProps {
  /** The interview session to display. */
  session: InterviewSession | null;
  /** Total number of responses. */
  responseCount: number;
  /** Number of completed responses. */
  completedCount: number;
  /** Campaign destinations, kept inside the sticky header. */
  navigation?: React.ReactNode;
  /** Contextual editor actions share the persistent campaign navigation row. */
  contextualToolbar?: React.ReactNode;
  /** Called when the user requests to close the campaign. */
  onClose: () => void;
  /** Called when the user requests to re-open a closed campaign. */
  onReopen?: () => void;
  /** Called when the user opens the edit-configuration page. */
  onEdit?: () => void;
  /** Called when the user clones the campaign. */
  onClone: () => void;
  /** Called when the user requests to delete the campaign. */
  onDelete: () => void;
  /** Called when the user requests to delete an individual response. */
  onDeleteResponse?: () => void;
  /** Called to download the response report. Only shown when available. */
  onDownloadReport?: () => void;
  /** Called to open the ask-the-campaign panel. Only shown when available. */
  onAsk?: () => void;
  /**
   * Called to open the automation surface (enabling it first when none exists).
   * Gated on edit permission; absent means the entry point is hidden.
   */
  onAutomation?: () => void;
  /** Whether an automation flow already exists (labels the button and item). */
  hasAutomation?: boolean;
  /**
   * Disable the automation entry while its state is loading or an enable call
   * is in flight, so a click cannot fire before the flow status is known.
   */
  automationDisabled?: boolean;
  /** Currently selected response. */
  selectedResponse?: InterviewData;
  /** Open the InviteModal immediately on mount (e.g. after session creation). */
  initialInviteOpen?: boolean;
  /** Add people from the invite modal. */
  onInvitePeople?: AddParticipantsHandler;
}

/**
 * Compact sticky header bar for the campaign detail page.
 * Shows campaign name, status badge,
 * share button, and a consolidated dropdown for all secondary actions.
 */
export const CampaignHeader: React.FC<CampaignHeaderProps> = ({
  session,
  navigation,
  contextualToolbar,
  onClose,
  onReopen,
  onEdit,
  onClone,
  onDelete,
  onDeleteResponse,
  onDownloadReport,
  onAsk,
  onAutomation,
  hasAutomation,
  automationDisabled,
  selectedResponse,
  initialInviteOpen,
  onInvitePeople,
}) => {
  // Permission gating. Edit / Close / Reopen / Clone require
  // `interviews:edit`; Delete requires `interviews:delete`; Clone also
  // creates a new session. Viewers see only read-only actions.
  const { hasPermission } = useOrg();
  const canEdit = hasPermission('interviews:edit');
  const canCreate = hasPermission('interviews:create');
  const canDelete = hasPermission('interviews:delete');

  const [inviteModalOpen, setInviteModalOpen] = useState((initialInviteOpen ?? false) && canEdit);
  const [exportOpen, setExportOpen] = useState(false);
  const [preparedExport, setPreparedExport] = useState<PreparedCampaignExport>();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => { setExportOpen(false); setPreparedExport(undefined); }, [session?.id]);
  useEffect(() => {
    setInviteModalOpen(Boolean(session && initialInviteOpen && canEdit));
  }, [session?.id, initialInviteOpen, canEdit]);
  useEffect(() => {
    const encoded = searchParams.get('csv_export');
    if (!encoded || !session) return;
    try {
      const prepared = readPreparedExport(JSON.parse(encoded), session.id);
      if (prepared) { setPreparedExport(prepared); setExportOpen(true); }
    } catch { /* Malformed link preferences never trigger a download. */ }
    const next = new URLSearchParams(searchParams);
    next.delete('csv_export');
    setSearchParams(next, { replace: true });
  }, [searchParams, session?.id, setSearchParams]);
  const dropdownItems = session ? [
    ...(canEdit && onAutomation && !navigation
      ? [{
        label: 'Automation',
        onClick: () => onAutomation(),
        icon: <IconBolt className="w-4 h-4" />,
        className: 'text-muted-foreground',
        disabled: automationDisabled,
      }]
      : []),
    ...(onAsk
      ? [{
        label: 'Ask',
        onClick: () => onAsk(),
        icon: <IconMessage className="w-4 h-4" />,
        className: 'text-muted-foreground',
      }]
      : []),
    ...(onDownloadReport
      ? [{
        label: 'Download Response',
        onClick: () => onDownloadReport(),
        icon: <DownloadIcon className="w-4 h-4" />,
        className: 'text-muted-foreground',
      }]
      : []),
    {
      label: 'Export CSV',
      onClick: () => setExportOpen(true),
      icon: <DownloadIcon className="w-4 h-4" />,
      className: 'text-muted-foreground',
    },
    ...(canEdit && (getCampaignDisplayStatus(session) === 'active' || getCampaignDisplayStatus(session) === 'scheduled')
      ? [{
        label: 'Close',
        onClick: () => onClose(),
        icon: <IconBan className="w-4 h-4" />,
        className: 'text-muted-foreground',
      }]
      : []),
    ...(canEdit && getCampaignDisplayStatus(session) === 'completed' && onReopen
      ? [{
        label: 'Reopen',
        onClick: () => onReopen(),
        icon: <PlayIcon className="w-4 h-4" />,
        className: 'text-muted-foreground',
      }]
      : []),
    ...(canEdit && onEdit && !navigation
      ? [{
        label: 'Edit',
        onClick: () => onEdit(),
        icon: <IconPencil className="w-4 h-4" />,
        className: 'text-muted-foreground',
      }]
      : []),
    ...(canEdit && canCreate
      ? [{
        label: 'Clone',
        onClick: () => onClone(),
        icon: <IconCopy className="w-4 h-4" />,
        className: 'text-muted-foreground',
      }]
      : []),
    ...(canDelete && onDeleteResponse && (!selectedResponse || getResponseDisplayStatus(selectedResponse) !== 'in_progress')
      ? [{
        label: `Delete Response #${selectedResponse?.response_number || ''}`,
        onClick: () => onDeleteResponse(),
        icon: <IconTrash className="w-4 h-4" />,
        className: 'text-danger',
      }]
      : []),
    ...(canDelete
      ? [{
        label: 'Delete',
        onClick: () => onDelete(),
        icon: <IconTrash className="w-4 h-4" />,
        className: 'text-danger',
      }]
      : []),
  ] : [];

  return (
    <header aria-busy={!session} className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-16 md:top-0 z-20">
      <div className="container mx-auto px-4 py-3">
        <div data-campaign-primary-row className="flex items-center gap-2 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-h-9 min-w-0">
              <h1 className="min-w-0 text-xl font-semibold text-foreground truncate">{session?.target_name ?? <Skeleton width="12em" />}</h1>
              {session ? <StatusBadge type="session" status={getCampaignDisplayStatus(session)} /> : <span className="text-sm"><Skeleton width="5em" /></span>}
            </div>
          </div>
          {canEdit && onAutomation && !navigation && <button type="button" onClick={onAutomation} disabled={!session || automationDisabled}
            className="hidden md:flex shrink-0 cursor-pointer items-center gap-2 rounded-lg bg-surface px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40">
            <IconBolt className="h-4 w-4" />{hasAutomation ? 'Automation' : 'Automate'}
          </button>}
          {canEdit && (!session || (getCampaignDisplayStatus(session) !== 'completed' && getCampaignDisplayStatus(session) !== 'expired')) && <button type="button"
            onClick={() => setInviteModalOpen(true)}
            disabled={!session}
            className="flex justify-self-end shrink-0 cursor-pointer items-center gap-2 rounded-lg button-accent px-4 py-2 text-sm font-medium transition-colors">
            <IconUserPlus className="h-4 w-4" />Invite people
          </button>}
          <div className="shrink-0"><DropdownMenu trigger={<button type="button" disabled={!session} className="cursor-pointer p-2 hover:bg-surface-hover rounded-lg transition-colors" aria-label="Campaign actions">
            <IconDotsVertical className="w-5 h-5 text-muted-foreground" />
          </button>} items={dropdownItems} /></div>
        </div>
      </div>
      {(navigation || contextualToolbar) && <div data-campaign-command-row className="container mx-auto flex min-w-0 items-center justify-between gap-2 px-4">
        {navigation}
        {contextualToolbar}
      </div>}

      {session && exportOpen && (
        <React.Suspense fallback={null}>
          <CampaignExportModal key={session.id} session={session} initialFilters={preparedExport?.filters} conversationIds={preparedExport?.conversationIds} onClose={() => { setExportOpen(false); setPreparedExport(undefined); }} />
        </React.Suspense>
      )}

      {session && <InviteModal
        open={canEdit && inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        session={session}
        onInvitePeople={onInvitePeople}
        onOpenAutomation={canEdit ? onAutomation : undefined}
        automationDisabled={automationDisabled}
      />}
    </header>
  );
};
